/**
 * <LiveDevicesSheet> — « Choisis tes appareils », en pleine séance (template).
 *
 * La fenêtre que les chevrons du micro et de la caméra de la barre du live ouvraient sur le web
 * (`MediaDeviceSelectModal`), geste pour geste :
 *  - trois onglets, « Microphone », « Haut-parleur » et « Caméra » ; le chevron du micro l'ouvre sur
 *    le micro, celui de la caméra sur la caméra ;
 *  - au micro : son niveau qui bouge quand l'élève parle (`<LevelBars>`), « Supprimer les bruits de
 *    fond » quand le navigateur le permet, puis la liste ;
 *  - à la caméra : son aperçu (la fente `cameraPreview`), puis la liste ;
 *  - chaque liste : ses appareils, le premier « Par défaut du système », l'actif coché ; un seul
 *    appareil se lit coché ; aucun : « Aucun appareil disponible » ;
 *  - en Concentré, une phrase dit que le son est coupé mais que l'élève peut régler ses appareils ;
 *  - « Un problème ? » referme la fenêtre et ouvre l'aide.
 *
 * Une feuille basse sur un téléphone, une carte au milieu sur un grand écran (`<Modal>`). Sa hauteur
 * ne bouge pas d'un onglet à l'autre (Wajih, 20/10) : c'est celle du plus haut des trois. Les deux
 * onglets fermés se posent, invisibles et hors du lecteur d'écran, sous l'onglet ouvert, et le cadre
 * des onglets prend la hauteur du plus grand (une hauteur mesurée : un état d'interface). L'aperçu
 * de la caméra n'y est qu'une fente vide : la vidéo ne tourne que sur son onglet.
 *
 * Contrôlée : l'onglet, les listes, l'actif et le niveau arrivent en props ; les gestes remontent.
 */
import React, { useState } from "react"
import { View, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { LevelBars } from "../../atoms/LevelBars"
import { Switch } from "../../atoms/Switch"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"
import { OptionCard } from "../../molecules/OptionCard"

export type UILiveDeviceTab = "microphone" | "speaker" | "camera"

/** Un appareil de la liste : son nom (déjà traduit), et « Par défaut du système » pour le premier. */
export type UILiveDevice = { id: string; label: string; description?: string }

export type UILiveDeviceChoice = {
    devices: UILiveDevice[]
    activeId: string | null
    onSelect: (id: string) => void
}

export type LiveDevicesSheetProps = {
    visible: boolean
    onClose: () => void
    tab: UILiveDeviceTab
    onChangeTab: (tab: UILiveDeviceTab) => void
    microphone: UILiveDeviceChoice
    camera: UILiveDeviceChoice
    speaker: UILiveDeviceChoice
    /** Le niveau du micro, de 0 à 1, relu par l'écran tant que l'onglet est ouvert. */
    micLevel?: number
    /** « Supprimer les bruits de fond ». Absent : le navigateur ne le permet pas. */
    noiseFilter?: { enabled: boolean; pending?: boolean; onChange: (enabled: boolean) => void }
    /** L'aperçu de ma caméra (`VideoView` de LiveKit), sur l'onglet de la caméra. */
    cameraPreview?: React.ReactNode
    /** En Concentré : la phrase qui dit que le son est coupé. Défaut `false`. */
    focused?: boolean
    /** « Un problème ? » : la fenêtre se ferme, l'aide s'ouvre. Absent : pas de lien. */
    onOpenHelp?: () => void
    testID?: string
}

const TEXTS = {
    title: "Choisis tes appareils",
    microphone: "Microphone",
    speaker: "Haut-parleur",
    camera: "Caméra",
    none: "Aucun appareil disponible",
    noise: "Supprimer les bruits de fond",
    noiseHint: "Réduis les bruits autour de toi",
    focused: "Mode Concentré : le son est coupé. Tu peux quand même régler tes appareils.",
    level: "Le niveau de ton micro",
    help: "Un problème ?",
}

const TABS: { key: UILiveDeviceTab; label: string; icon: IconName }[] = [
    { key: "microphone", label: TEXTS.microphone, icon: "microphone" },
    { key: "speaker", label: TEXTS.speaker, icon: "volume-up" },
    { key: "camera", label: TEXTS.camera, icon: "video" },
]

function DeviceList({ choice, icon, onClose }: { choice: UILiveDeviceChoice; icon: IconName; onClose: () => void }) {
    if (choice.devices.length === 0) {
        return (
            <Text size="text-md" color="text-muted" center>
                {TEXTS.none}
            </Text>
        )
    }
    // Un seul appareil : il est l'actif, le toucher referme (`MediaDeviceSelectModal`).
    const single = choice.devices.length === 1
    return (
        <Stack gap="2">
            {choice.devices.map((device) => (
                <OptionCard
                    key={device.id}
                    label={device.label}
                    description={device.description}
                    selected={single || device.id === choice.activeId}
                    leading={<Icon name={icon} size={20} color="primary" />}
                    onPress={() => (single ? onClose() : choice.onSelect(device.id))}
                />
            ))}
        </Stack>
    )
}

export function LiveDevicesSheet(props: LiveDevicesSheetProps) {
    const {
        visible,
        onClose,
        tab,
        onChangeTab,
        microphone,
        camera,
        speaker,
        micLevel = 0,
        noiseFilter,
        cameraPreview,
        focused = false,
        onOpenHelp,
        testID,
    } = props
    // La hauteur de chaque onglet, mesurée : le cadre prend la plus grande, et ne bouge plus.
    const [heights, setHeights] = useState<Partial<Record<UILiveDeviceTab, number>>>({})
    const measure = (key: UILiveDeviceTab, event: LayoutChangeEvent) => {
        const height = event.nativeEvent.layout.height
        setHeights((all) => (all[key] === height ? all : { ...all, [key]: height }))
    }
    const tallest = Math.max(0, ...Object.values(heights).map((height) => height ?? 0))

    // `open` : l'onglet à l'écran. Fermé, il ne sert qu'à se mesurer — l'aperçu de la caméra y est
    // une fente vide de la même taille.
    const panel = (key: UILiveDeviceTab, open: boolean) => {
        if (key === "microphone") {
            return (
                <Stack gap="3">
                    <View style={styles.level}>
                        <LevelBars level={open ? micLevel : 0} accessibilityLabel={TEXTS.level} />
                    </View>
                    {noiseFilter != null && (
                        <Switch
                            checked={noiseFilter.enabled}
                            onChange={noiseFilter.onChange}
                            disabled={noiseFilter.pending === true}
                            label={`${TEXTS.noise} — ${TEXTS.noiseHint}`}
                        />
                    )}
                    <DeviceList choice={microphone} icon="microphone" onClose={onClose} />
                </Stack>
            )
        }
        if (key === "speaker") return <DeviceList choice={speaker} icon="volume-up" onClose={onClose} />
        return (
            <Stack gap="3">
                {cameraPreview != null && <View style={styles.preview}>{open ? cameraPreview : null}</View>}
                <DeviceList choice={camera} icon="video" onClose={onClose} />
            </Stack>
        )
    }

    return (
        <Modal visible={visible} onClose={onClose} title={TEXTS.title} sizeToContent testID={testID}>
            <Stack gap="4">
                {focused && <Banner icon="volume-mute" color="warning-strong" align="start" message={TEXTS.focused} />}
                <Row gap="2" align="center" style={styles.tabs}>
                    {TABS.map((item) => (
                        <ChoiceChip
                            key={item.key}
                            variant="pill"
                            iconName={item.icon}
                            label={item.label}
                            selected={tab === item.key}
                            onPress={() => onChangeTab(item.key)}
                        />
                    ))}
                </Row>

                <View style={styles.panels(tallest)}>
                    {TABS.map((item) => {
                        const open = item.key === tab
                        return (
                            <View
                                key={item.key}
                                onLayout={(event) => measure(item.key, event)}
                                style={open ? undefined : styles.hiddenPanel}
                                pointerEvents={open ? "auto" : "none"}
                                aria-hidden={!open}
                                accessibilityElementsHidden={!open}
                                importantForAccessibility={open ? "auto" : "no-hide-descendants"}
                            >
                                {panel(item.key, open)}
                            </View>
                        )
                    })}
                </View>

                {onOpenHelp != null && (
                    <View style={styles.help}>
                        <TextButton
                            label={TEXTS.help}
                            onPress={() => {
                                onClose()
                                onOpenHelp()
                            }}
                        />
                    </View>
                )}
            </Stack>
        </Modal>
    )
}

const styles = StyleSheet.create((theme) => ({
    level: { alignItems: "center", paddingVertical: theme.spacing["2"] },
    /** L'aperçu au format d'une vidéo, comme la tuile de la scène. */
    preview: {
        aspectRatio: 16 / 9,
        borderRadius: theme.radius.lg,
        overflow: "hidden",
        backgroundColor: theme.colors["surface-subtle"],
    },
    help: { alignItems: "center" },
    /** Le cadre des onglets : la hauteur du plus haut, mesurée. */
    panels: (tallest: number) => ({ minHeight: tallest }),
    /**
     * Un onglet fermé : posé sous l'ouvert, à sa largeur, pour se mesurer sans se voir. Sur le web,
     * `visibility` le sort aussi de l'ordre de tabulation : ses boutons ne prennent pas le focus.
     */
    hiddenPanel: { position: "absolute", top: 0, left: 0, right: 0, opacity: 0, _web: { visibility: "hidden" } },
    tabs: { flexWrap: "wrap" },
}))
